import {FieldType} from '../../../models/field-type';
import {PartyTypeCode} from '../../../../core/consts/party-type-code.enum';
import {OwnerDataConsts} from '../../../../core/consts/owner-data.consts';
import {
  OwnerDataStateService
} from '../../../../core/services/impl/section-services/owner-data/owner-data-state.service';
import {getDrivingLicenseIssueYears} from '../../../utils/driving-license-years';
import {OwnerDataSectionConfig} from './owner-data-section.config';
import {FormConfig} from '../../../models/form-config';
import {
  PredefinedValuesService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {AgreementModel} from '../../../../core/models/agreement.model';
import {FieldConfig, FieldNameSource} from '../../../models/field-config';
import {AgreementsService} from '../../../services/agreements.service';
import {LanguageService} from '../../../services/language.service';
import {Validator} from '../../../models/validator.enum';
import {isCarOne, isNext} from '../../../../core/utils/utils';
import {MaterialDatepickerType} from '../../../enums/material-datepicker-type.enum';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';
import {DatepickerStartView} from '../../../enums/datepicker-start-view.enum';

export class OwnerDataConfig extends OwnerDataSectionConfig {

  isNext = isNext();

  constructor(private stateService: OwnerDataStateService,
              private predefined: PredefinedValuesService,
              private agreementsService: AgreementsService,
              private languageService: LanguageService,
              private regExpService: RegExpService) {
    super();
  };

  createOwnerConfig(): FormConfig {
    const formConfig =  <FormConfig> {
      formKey: 'OWNER_DATA_SECTION',
      fieldsConfig: [
        {
          fieldKey: OwnerDataConsts.PARTY_CODE_TYPE,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: this.buildPartyTypeCodeRadioConfig(),
        },
        {
          fieldKey: OwnerDataConsts.COMPANY_NAME,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.companyName',
          type: FieldType.TextField,
          personalData: true,
          displayInPreviewMode: true,
          validators: [
            Validator.REQUIRED_NOT_BLANK,
            {
              name: Validator.MAX_LENGTH,
              maxLength: 100
            },
            {
              name: Validator.MIN_LENGTH,
              minLength: 2
            }],
          autocomplete: 'organization',
          properties: {
            textTransform: {
              type: 'capitalize'
            },
          }
        },
        {
          fieldKey: OwnerDataConsts.NIP,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.nip',
          type: FieldType.TextField,
          personalData: true,
          displayInPreviewMode: true,
          validators: [Validator.REQUIRED, Validator.NIP],
          maskConfig: {
            mask: '000-000-00-00',
            dropSpecialCharacters: true
          },
          properties: {
            mobileInputType: 'tel',
          }
        },
        {
          fieldKey: OwnerDataConsts.FIRST_NAME,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.firstName',
          captionForPreview: 'OWNER_DATA_SECTION.FORM_FIELDS.firstName',
          type: FieldType.TextField,
          personalData: true,
          displayInPreviewMode: true,
          validators: [
            Validator.REQUIRED,
            {
              name: Validator.PATTERN,
              pattern: this.regExpService.getRegExp(RegExpKey.FIRST_NAME)
            },
            {
              name: Validator.MAX_LENGTH,
              maxLength: 100
            },
            {
              name: Validator.MIN_LENGTH,
              minLength: 2
            }],
          autocomplete: 'given-name',
          properties: {
            textTransform: {
              type: 'capitalize'
            },
          }
        },
        {
          fieldKey: OwnerDataConsts.LAST_NAME,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.lastName',
          captionForPreview: 'OWNER_DATA_SECTION.FORM_FIELDS.lastName',
          type: FieldType.TextField,
          personalData: true,
          displayInPreviewMode: true,
          validators: [
            Validator.REQUIRED,
            {
              name: Validator.PATTERN,
              pattern: this.regExpService.getRegExp(RegExpKey.LAST_NAME)
            },
            {
              name: Validator.MAX_LENGTH,
              maxLength: 100
            },
            {
              name: Validator.MIN_LENGTH,
              minLength: 2
            }],
          autocomplete: 'family-name',
          properties: {
            textTransform: {
              type: 'capitalize'
            },
          }
        },
        {
          fieldKey: OwnerDataConsts.PESEL,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.pesel',
          captionForPreview: 'OWNER_DATA_SECTION.FORM_FIELDS.pesel',
          // description: 'OWNER_DATA_SECTION.TOOLTIP.pesel',
          personalData: true,
          displayInPreviewMode: true,
          type: FieldType.TextField,
          validators: [
            Validator.REQUIRED,
            Validator.PESEL,
            Validator.LEGAL_AGE_PESEL
          ],
          maskConfig: {
            mask: '00000000000'
          },
          properties: {
            mobileInputType: 'tel',
          }
        },
        {
          fieldKey: OwnerDataConsts.BIRTH_DATE,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.birthDate',
          type: FieldType.DateField,
          // validators: [Validator.REQUIRED],
          materialDateFieldConfig: {},
          analyticsConfig: {
            skipFieldValue: true
          }
        },
        {
          fieldKey: OwnerDataConsts.POSTAL_CODE,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.postalCode',
          type: FieldType.TextField,
          displayInPreviewMode: true,
          validators: [
            Validator.REQUIRED_NOT_BLANK,
            Validator.POSTAL_CODE,
            {
              name: Validator.PATTERN,
              pattern: this.regExpService.getRegExp(RegExpKey.POSTAL_CODE)
            }],
          maskConfig: {
            mask: '00-000',
            dropSpecialCharacters: false
          },
          autocomplete: 'postal-code',
          properties: {
            mobileInputType: 'tel',
          },
          analyticsConfig: {
            includeFieldValue: true
          }
        },
        {
          fieldKey: OwnerDataConsts.MATRIMONIAL_STATUS,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.matrimonialStatus',
          type: FieldType.Select,
          displayInEditableMode: false,
          displayInPreviewMode: true,
          validators: [Validator.REQUIRED],
          options: {
            item: 'key',
            label: { label: 'label' },
            items: this.stateService.matrimonialStatusDictionary.items
          },
          select: {
            dropdownPosition: 'bottom',
            multiple: false,
            searchable: true
          }
        },
        {
          fieldKey: OwnerDataConsts.OLDEST_YOUNG_CHILD_YEAR_OF_BIRTH,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.oldestYoungChildYearOfBirth',
          type: FieldType.DateField,
          validators: [],
          displayInPreviewMode: true,
          materialDateFieldConfig: {
            startView: 'multi-year',
            type: MaterialDatepickerType.YEAR,
            datepickerToggleIcon: 'arrow_drop_down',
            hideCalendarPeriodButton: true,
            initMinDateWithStartAt: false,
            minDate: Math.min(...this.stateService.oldestYoungChildYearOfBirthDictionary) + '',
            maxDate: Math.max(...this.stateService.oldestYoungChildYearOfBirthDictionary) + '',
            manualInputAllowed: true,
            readonly: false,
          }
        },
        {
          fieldKey: OwnerDataConsts.CO_OWNERS,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          displayInPreviewMode: true,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.coOwners',
          validators: [Validator.REQUIRED],
          radioConfig: [
            {value: 0, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.none'},
            {value: 1, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.one'},
            {value: 2, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.more'}
          ],
        },
        {
          fieldKey: OwnerDataConsts.ADDITIONAL_DRIVERS,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          displayInPreviewMode: true,
          caption: 'OWNER_DATA_SECTION.FORM_FIELDS.additionalDrivers',
          validators: [Validator.REQUIRED],
          radioConfig: [
            {value: 0, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.none'},
            {value: 1, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.one'},
            {value: 2, label: 'OWNER_DATA_SECTION.FORM_FIELDS.CO_OWNER.more'}
          ],
        }
      ]
    };

    if (this.stateService.drivingLicenseIssueYearVisible) {
      formConfig.fieldsConfig.push({
        fieldKey: OwnerDataConsts.DRIVING_LICENSE_ISSUE_YEAR,
        caption: 'OWNER_DATA_SECTION.FORM_FIELDS.drivingLicenseIssueYear',
        type: FieldType.DateField,
        validators: this.getDrivingLicenseValidators(),
        displayInPreviewMode: true,
        materialDateFieldConfig: {
          startView: 'multi-year' as DatepickerStartView,
          type: MaterialDatepickerType.YEAR,
          startAt: () => this.stateService.DRIVING_LICENSE_ISSUE_YEAR_START_YEAR,
          datepickerToggleIcon: 'arrow_drop_down',
          hideCalendarPeriodButton: true,
          initMinDateWithStartAt: false,
          minDate: Math.min(...getDrivingLicenseIssueYears(OwnerDataConfig.START_YEAR)) + '',
          maxDate: Math.max(...getDrivingLicenseIssueYears(OwnerDataConfig.START_YEAR)) + '',
          manualInputAllowed: true,
          readonly: false,
        }
      },
      {
        fieldKey: OwnerDataConsts.NO_DRIVING_LICENSE,
        caption: 'OWNER_DATA_SECTION.FORM_FIELDS.noDrivingLicense',
        type: FieldType.Checkbox,
        displayInEditableMode: false,
        checkboxConfig: {
          readonly: false
        },
        analyticsConfig: {
          customFieldName: 'OWNER_DATA_SECTION.FORM_FIELDS.drivingLicenseIssueYear',
          customFieldValue: () => {
            if (this.stateService.noDrivingLicense) {
              return this.languageService.translateService.instant('OWNER_DATA_SECTION.FORM_FIELDS.noDrivingLicense');
            }
            return this.stateService.ownerData.drivingLicenseIssueYear;
          }
        }
      });
    }

    if (this.stateService.drivingLicenseIssueMonthVisible) {
      formConfig.fieldsConfig.push({
        fieldKey: OwnerDataConsts.DRIVING_LICENSE_ISSUE_MONTH,
        caption: 'OWNER_DATA_SECTION.FORM_FIELDS.drivingLicenseIssueMonth',
        type: FieldType.Select,
        displayInEditableMode: false,
        displayInPreviewMode: true,
        validators: [],
        options: {
          item: 'key',
          label: { label: 'label' },
          items: []
        },
        select: {
          dropdownPosition: 'bottom',
          multiple: false,
          searchable: true
        }
      });
    }

    if (isCarOne()) {
      formConfig.fieldsConfig.push({
        fieldKey: OwnerDataConsts.CHILDREN_UNDER_26,
        caption: 'OWNER_DATA_SECTION.FORM_FIELDS.childrenUnder26',
        type: FieldType.ButtonToggle,
        validators: [Validator.REQUIRED],
        displayInPreviewMode: true,
        radioInline: true,
        radioConfig: [
          {
            value: false,
            label: 'no'
          },
          {
            value: true,
            label: 'yes'
          }
        ],
        initialValue: false,
        analyticsConfig: {
          fieldNameSource: FieldNameSource.CAPTION
        }
      });
    } else {
      formConfig.fieldsConfig.push({
        fieldKey: OwnerDataConsts.CHILDREN_UNDER_26,
        caption: 'OWNER_DATA_SECTION.FORM_FIELDS.childrenUnder26',
        type: FieldType.Checkbox,
        displayInEditableMode: true,
        checkboxConfig: {
          readonly: false
        },
        analyticsConfig: {
          fieldNameSource: FieldNameSource.CAPTION
        }
      });
    }

    formConfig.fieldsConfig.forEach(fieldConfig => {
      this.predefined.addPredefinedValue(
        fieldConfig,
        this.predefined.values,
        formConfig.formKey,
        fieldConfig.fieldKey
      )
    });

    return this.createConfig(formConfig)
  }

  createAgreementConfig(agreement: AgreementModel): FieldConfig {
    return {
      fieldKey: agreement.code,
      fieldsDataFormat: 'objectArray',
      captionAsHTML: this.getAgreementValue(agreement).shortValue ? this.getAgreementValue(agreement).shortValue : 'No short value in agreement ' + agreement.code,
      type: FieldType.Checkbox,
      predefined: {
        isPredefined: true,
        additionalInfo: 'This is additional information email'
      },
      validators: agreement.details.required ? [Validator.REQUIRED_TRUE] : [],
      beanConfig: {
        propertyName: 'agreements',
        keyPropertyName: 'code',
      },
      checkboxConfig: {
        readonly: false,
        expand: {
          expandedLabel: 'SHARED.AGREEMENTS.showAgreementFullText',
          collapsedLabel: 'SHARED.AGREEMENTS.hideAgreementFullText',
          expandText: this.getAgreementValue(agreement).value
        },
        mappingConfig: {
          propertyName: 'selectedAnswer',
          valuesMapping: this.agreementsService.getValuesMapping(agreement)
        }
      },
    };
  }

  private getAgreementValue(agreement: any): any {
    const language = this.languageService.getDefaultLanguage().toUpperCase();
    const agreementValues = agreement.details.values.filter(item => item.languageCode.toUpperCase() === language);
    if (agreementValues.length > 0) {
      return agreementValues[0];
    } else {
      throw new Error(`Agreement does not contain value for given language code: ${language}.`);
    }
  }
  private buildPartyTypeCodeRadioConfig(): any[] {
    const radioConfig = [
      {value: PartyTypeCode.NATURAL_PERSON, label: 'OWNER_DATA_SECTION.FORM_FIELDS.naturalPerson'},
      {
        value: PartyTypeCode.BUSINESS_ACTIVITY,
        label: 'OWNER_DATA_SECTION.FORM_FIELDS.businessActivity'
      }
    ];
    if (this.stateService.showCompanyPartyTypeCode) {
      radioConfig.push({value: PartyTypeCode.ORGANIZATION, label: 'OWNER_DATA_SECTION.FORM_FIELDS.company'});
    }
    return radioConfig;
  }

  private getDrivingLicenseValidators(): any[] {
    return this.stateService.noDrivingLicense ? [] : [Validator.REQUIRED];
  }
}
